import { JournalV1 } from "../../grader/journal/v1.grader";
import { ExtractInput } from "../extract-input";
import { ExtractOutput } from "../extract-output";
import { extractTable } from "../utils/extract-table";
import {
  extractValueAndConfigFromCell,
} from "../utils/extract-value-from-cell";

/**
 * extractJournalV1
 *
 * 주어진 ExtractInput 내 'table.journal'을 찾아 학생이 작성한 분개표 데이터를 추출,
 * 구조화하여 ExtractOutput<JournalV1[]> 형태로 반환합니다.
 *
 * 📌 사용 목적:
 * - 학생이 제출한 HTML 기반 분개표 데이터를 자동 채점/검증 가능하게 변환
 * - 채점 로직의 입력값(answerSheet)으로 사용하기 위함
 *
 * @param extractInput - ExtractInput 객체 (questionType, version, div 포함)
 * @returns ExtractOutput<JournalV1[]> - 추출된 분개 데이터 및 메타 정보
 */
export function extractJournalV1(
  extractInput: ExtractInput,
): ExtractOutput<JournalV1[]> {
  const { questionType, version, div } = extractInput;

  // 1️⃣ 'table.journal' 요소를 div 또는 HTML 문자열에서 추출
  const table = extractTable(div, "table.journal");
  if (!table) throw new Error("테이블을 만들어주세요."); // table이 없을 경우 에러 반환

  // 2️⃣ 테이블 내의 모든 <tr> 요소(행) 가져오기
  const trs = table.getElementsByTagName("tr");

  const journals: JournalV1[] = [];

  // 3️⃣ 헤더를 제외한 실제 데이터 행만 순회하며 추출
  for (const tr of Array.from(trs).slice(1)) {
    const tds = tr.getElementsByTagName("td");
    if (tds.length === 0) throw new Error("테이블 column을 확인해주세요."); // 열이 없으면 구조 문제

    // 4️⃣ 현재 행에 <input> 또는 <select>가 포함되어 있는지 여부 확인
    const isInput = tr.querySelectorAll("input, select").length > 0;

    /**
     * 5️⃣ 차변/대변 판별 기준:
     * - 차변:
     *   두 번째 td(tds[1])의 colSpan이 2인 경우
     *   사용 컬럼: tds[1] (계정명), tds[2] (금액)
     * - 대변:
     *   colSpan이 없고
     *   사용 컬럼: tds[2] (계정명), tds[4] (금액)
     */
    const firstTd = tds[1];

    if (firstTd && firstTd.colSpan === 2) {
      // ✅ 차변 행 처리

      // 방어적 체크 (HTML 구조 깨짐 방지)
      if (!tds[1]) throw new Error("알수없는 문제가 생겼습니다."); // 계정명 컬럼 유실
      if (!tds[2]) throw new Error("알수없는 문제가 생겼습니다."); // 금액 컬럼 유실

      // 계정명과 금액에서 값과 answerConfig 추출
      const nameData = extractValueAndConfigFromCell(tds[1]);
      const valueData = extractValueAndConfigFromCell(tds[2]);

      journals.push({
        id: tr.id, // 행의 고유 ID
        side: "debit" as const, // 차변 표시
        name: nameData.value, // 계정명 추출
        value: valueData.value, // 금액 추출
        isInput, // 입력 가능 여부
        answerConfig: nameData.answerConfig || valueData.answerConfig, // 🆕 name 또는 value 필드의 answerConfig
      });
    } else {
      // ✅ 대변 행 처리

      // 방어적 체크
      if (!tds[2]) throw new Error("알수없는 문제가 생겼습니다."); // 계정명 컬럼 유실
      if (!tds[4]) throw new Error("알수없는 문제가 생겼습니다."); // 금액 컬럼 유실

      // 계정명과 금액에서 값과 answerConfig 추출
      const nameData = extractValueAndConfigFromCell(tds[2]);
      const valueData = extractValueAndConfigFromCell(tds[4]);

      journals.push({
        id: tr.id, // 행의 고유 ID
        side: "credit" as const, // 대변 표시
        name: nameData.value, // 계정명 추출
        value: valueData.value, // 금액 추출
        isInput, // 입력 가능 여부
        answerConfig: nameData.answerConfig || valueData.answerConfig, // 🆕 name 또는 value 필드의 answerConfig
      });
    }
  }

  // 6️⃣ 추출된 데이터를 ExtractOutput 형태로 반환
  return {
    version, // 문제 버전
    questionType, // 문제 유형
    entryCount: journals.length, // 추출된 데이터 개수
    answerSheet: journals, // 추출된 JournalV1[] 데이터
  };
}
